import LanguageSelect from "./LanguageSelect";
import {
  MainContainer,
  Navbar,
  LogoSection,
  IntroContainer,
  ExploreSection,
  ButtonBlack,
  ButtonWhite,
  ScheduleSection,
  ScheduleInnerContainer,
  CarouselContainer,
  ProgressBarContainer,
  ProgressBar,
  DaysButtonsContainer,
  DayButton,
  ScheduleContainer,
  ScheduleScrollArea,
  YourJourneyContainer,
  YourJourneySection,
  BoboAccountSection,
  AdditionalInfoSection,
  FooterSection,
  AdditionalInfoContent
} from "./styles";
import mainLogo from "./img/main-logo.png";
import exploreImage from "./img/explore.png";
import { useState, useEffect } from "react";
import slide1 from "./img/slide1.png";
import slide2 from "./img/slide2.png";
import slide3 from "./img/slide3.png";
import slide4 from "./img/slide4.png";
import slide5 from "./img/slide5.png";
import slide6 from "./img/slide6.png";
import slide7 from "./img/slide7.png";
import yourJourneyImage from "./img/your-journey.png";
import yourAccountImage from "./img/your-account.png";
import wearableImage from "./img/wearable.png";
import appStoreImage from "./img/app-store.svg";
import googlePlayImage from "./img/google-play.svg";
import instagramImage from "./img/instagram.svg";
import linkedinImage from "./img/linkedin.svg";
import footerLogo from "./img/footer-logo.svg";

import scheduleEn from "./schedule-en.json";
import scheduleDe from "./schedule-de.json";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getBalanceAndPin } from "../../features/balance-and-pin/balanceAndPinSlice";
import Spinner from "../../components/Spinner";
import { formattedBalance } from "../../features/balance-and-pin/utils";

const JapanTirol = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [activeDay, setActiveDay] = useState(1);

  const slides = [slide1, slide2, slide3, slide4, slide5, slide6, slide7];

  const { t, i18n } = useTranslation();

  const { balanceAndPin, isLoading } = useSelector(
    (state) => state.balanceAndPin
  );

  const dispatch = useDispatch();

  // Select schedule based on language
  const schedule = i18n.language === "de" ? scheduleDe : scheduleEn;

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: "?provider=pannovate"
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch]);

  useEffect(() => {
    setProgress(0);
    setImgLoaded(false);
    const step = 100 / 30; // 3s, update every 0.1s
    let percent = 0;
    const interval = setInterval(() => {
      percent += step;
      setProgress(percent);
      if (percent >= 100) {
        setProgress(100);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [current]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 3000); // 3 seconds per slide
    return () => clearInterval(interval);
  }, [slides.length]);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <MainContainer>
      <Navbar>
        <div className="balance-container">
          <p>{t("japanTyrol.currentBalance")}</p>
          <p>
            {formattedBalance(
              balanceAndPin?.balanceDetails?.availableBalance,
              balanceAndPin?.balanceDetails?.currency
            )}
          </p>
        </div>
        <LanguageSelect />
      </Navbar>
      <LogoSection>
        <img src={mainLogo} alt="logo" />
      </LogoSection>
      <IntroContainer>
        <div className="header-container">
          <h1>{t("japanTyrol.japan")}</h1>
          <span className="header-text">{t("japanTyrol.meetsTirol")}</span>
        </div>
        <p>{t("japanTyrol.eventDate")}</p>
      </IntroContainer>
      <ExploreSection>
        <img src={exploreImage} alt="explore" />
        <div className="text-container">
          <h2 className="italic-text">{t("japanTyrol.exceptionalEvent")}</h2>
          <h2>{t("japanTyrol.forBodyAndSoul")}</h2>
        </div>
        <p>{t("japanTyrol.experienceDescription")}</p>
        <ButtonBlack
          onClick={() =>
            window.open(
              `https://www.bobofin.com/bobo-famex/family-wallet`,
              "_blank"
            )
          }
        >
          {t("japanTyrol.exploreMore")}
        </ButtonBlack>
        <ButtonWhite
          onClick={() =>
            window.open(
              `https://s3.eu-west-2.amazonaws.com/www.digiseq.co.uk/bobo/bobo-overview.pdf`,
              "_blank"
            )
          }
        >
          {t("japanTyrol.downloadBrochure")}
        </ButtonWhite>
      </ExploreSection>
      <ScheduleSection>
        <ScheduleInnerContainer>
          <CarouselContainer>
            <img
              className={`slide-image${imgLoaded ? " visible" : ""}`}
              src={slides[current]}
              alt={`slide${current + 1}`}
              onLoad={() => setImgLoaded(true)}
            />
            <ProgressBarContainer>
              <ProgressBar style={{ width: `${progress}%` }} />
            </ProgressBarContainer>
          </CarouselContainer>
          <h3>{t("japanTyrol.mainProgram")}</h3>
          <h2>{t("japanTyrol.japanMeetsTirol2025")}</h2>
          <DaysButtonsContainer>
            <DayButton active={activeDay === 1} onClick={() => setActiveDay(1)}>
              {t("japanTyrol.day1")}
            </DayButton>
            <DayButton active={activeDay === 2} onClick={() => setActiveDay(2)}>
              {t("japanTyrol.day2")}
            </DayButton>
            <DayButton active={activeDay === 3} onClick={() => setActiveDay(3)}>
              {t("japanTyrol.day3")}
            </DayButton>
          </DaysButtonsContainer>
          <ScheduleContainer>
            <ScheduleScrollArea>
              {schedule[`day${activeDay}`].map((day, dayIndex) => (
                <div key={dayIndex}>
                  {/* Date */}
                  {day.date && <div className="date">{day.date}</div>}
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #fff",
                      margin: "16px 0"
                    }}
                  />
                  {/* Main Title (italic) */}
                  <div
                    className="title italic-text"
                    style={{ marginBottom: 16 }}
                  >
                    {day.title}
                  </div>
                  <hr
                    style={{
                      border: "none",
                      borderTop: "1px solid #fff",
                      margin: "16px 0"
                    }}
                  />
                  {/* Locations */}
                  {day.locations &&
                    day.locations.map((location, locIdx) => (
                      <div key={locIdx} style={{ marginBottom: 24 }}>
                        {/* Location name */}
                        <div className="title" style={{ marginBottom: 8 }}>
                          {location.location}
                        </div>
                        {/* Events */}
                        {location.events &&
                          location.events.map((event, eventIdx) => (
                            <div
                              className="event-container"
                              key={eventIdx}
                              style={{ marginBottom: 12 }}
                            >
                              <div className="time">{event.time}</div>
                              <div className="details">{event.title}</div>
                              {event.details && event.details.length > 0 && (
                                <div className="details">
                                  {event.details.map((detail, detailIdx) => (
                                    <div key={detailIdx}>• {detail}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    ))}
                </div>
              ))}
            </ScheduleScrollArea>
          </ScheduleContainer>
        </ScheduleInnerContainer>
      </ScheduleSection>
      <YourJourneySection>
        <YourJourneyContainer>
          <div className="header-container">
            <h2>
              {t("japanTyrol.your")}{" "}
              <span className="italic-text">{t("japanTyrol.journey")}</span>
            </h2>
            <h2>{t("japanTyrol.withUs")}</h2>
          </div>
          <p>{t("japanTyrol.journeyDescription")}</p>
          <img src={yourJourneyImage} alt="your journey" />
        </YourJourneyContainer>
      </YourJourneySection>
      <BoboAccountSection>
        <div className="header-container">
          <img src={yourAccountImage} alt="your account" />
          <h2>
            <span className="italic-text">
              {t("japanTyrol.connectYourKeyfob")}
            </span>
          </h2>
          <h2>{t("japanTyrol.toBoboAccount")}</h2>
        </div>
        <p>{t("japanTyrol.downloadAppText")}</p>
        <p>{t("japanTyrol.contactSupportText")}</p>
        <img src={wearableImage} alt="wearable" />
        <p className="info-text">{t("japanTyrol.activationCodeText")}</p>
        <div className="activation-code-value">
          {balanceAndPin?.activationCode || "N/A"}
        </div>
        <p className="info-text">{t("japanTyrol.downloadAppText2")}</p>

        <div className="app-store-container">
          <img
            src={appStoreImage}
            alt="app store"
            onClick={() =>
              window.open(
                `https://apps.apple.com/us/app/bourgeois-boheme/id1574430730`,
                "_blank"
              )
            }
          />
          <img
            src={googlePlayImage}
            alt="google play"
            onClick={() =>
              window.open(
                `https://play.google.com/store/apps/details?id=com.paymeswiss.bobofin`,
                "_blank"
              )
            }
          />
        </div>
      </BoboAccountSection>
      <AdditionalInfoSection></AdditionalInfoSection>
      <AdditionalInfoContent>
        <div className="inner-container">
          <h2 className="italic-text">{t("japanTyrol.additionalInfoText")}</h2>
          <div
            onClick={() => {
              window.open(`https://www.bobofin.com/become-a-member`, "_blank");
            }}
            className="button"
          >
            {t("japanTyrol.joinUs")}
          </div>
        </div>
      </AdditionalInfoContent>
      <FooterSection>
        <div className="social-container">
          <img
            src={instagramImage}
            alt="instagram"
            onClick={() =>
              window.open(
                `https://www.instagram.com/bobo_network?igsh=MTk0N3hmZWc5dnNmZg==`,
                "_blank"
              )
            }
          />
          <img
            src={linkedinImage}
            alt="linkedin"
            onClick={() =>
              window.open(
                `https://www.linkedin.com/company/bourgeoisboheme/`,
                "_blank"
              )
            }
          />
        </div>
        <div className="footer-text-container">
          <p
            onClick={() =>
              window.open(
                `https://www.bobofin.com/terms-and-conditions`,
                "_blank noopener noreferrer"
              )
            }
          >
            {t("japanTyrol.termsAndConditions")}
          </p>
          <p
            onClick={() =>
              window.open(
                `https://www.bobofin.com/privacy-policy-cookies-policy`,
                "_blank noopener noreferrer"
              )
            }
          >
            {t("japanTyrol.privacyPolicy")}
          </p>
        </div>
        <img src={footerLogo} alt="footer logo" />
      </FooterSection>
    </MainContainer>
  );
};

export default JapanTirol;
